import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Package, PauseCircle, PlayCircle, Plus, Repeat, XCircle } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions } from '@/features/crm/api';
import { usePagination } from '@/hooks/use-pagination';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import {
  useCancelSubscription,
  useCreateSubscription,
  usePauseSubscription,
  useProducts,
  useSavePlan,
  useSaveProduct,
  useSubscriptions,
} from './api';
import {
  BILLING_CYCLE_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_VARIANT,
} from './labels';
import type { BillingCycle, Subscription } from './types';

const TABS = [
  ['subscriptions', 'Subscriptions'],
  ['catalogue', 'Products and plans'],
] as const;

export function SubscriptionsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'subscriptions';

  return (
    <div>
      <PageHeader
        title="Recurring revenue"
        description="What clients pay on a schedule, and the plans they are on."
      />

      <div className="mb-5 flex gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'subscriptions' ? <SubscriptionsTab /> : <CatalogueTab />}
    </div>
  );
}

function SubscriptionsTab() {
  const pagination = usePagination();
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<Subscription | null>(null);

  const pause = usePauseSubscription();
  const { data, isLoading, error, refetch } = useSubscriptions({
    page: pagination.page,
    limit: pagination.limit,
    status: status || undefined,
  });

  const columns: Column<Subscription>[] = [
    {
      key: 'company',
      header: 'Client',
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.company.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.product.name} · {row.plan.name}
            {row.plan.version > 1 && ` v${row.plan.version}`}
          </div>
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Recurring',
      className: 'tabular text-sm font-medium',
      cell: (row) => (
        <span>
          {formatMoney(row.recurringTotal, row.currency)}
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            / {BILLING_CYCLE_LABELS[row.billingCycle].toLowerCase()}
          </span>
        </span>
      ),
    },
    {
      key: 'qty',
      header: 'Qty',
      className: 'tabular text-sm text-muted-foreground',
      cell: (row) => row.quantity,
    },
    {
      key: 'period',
      header: 'Current period',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => `${formatDate(row.currentPeriodStart)} – ${formatDate(row.currentPeriodEnd)}`,
    },
    {
      key: 'next',
      header: 'Next bill',
      className: 'whitespace-nowrap text-sm',
      cell: (row) => (row.nextBillingDate ? formatDate(row.nextBillingDate) : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <Badge variant={SUBSCRIPTION_STATUS_VARIANT[row.status]}>
            {SUBSCRIPTION_STATUS_LABELS[row.status]}
          </Badge>
          {row.cancelAt && !row.cancellationReason?.startsWith('_') && (
            <span className="text-[10px] text-muted-foreground">
              ends {formatDate(row.cancelAt)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) =>
        row.status === 'CANCELLED' || row.status === 'EXPIRED' ? null : (
          <Can permission="product.subscription.update">
            <span className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
              <button
                onClick={() => pause.mutate({ id: row.id, paused: row.status !== 'PAUSED' })}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                {row.status === 'PAUSED' ? (
                  <>
                    <PlayCircle className="h-3 w-3" />
                    Resume
                  </>
                ) : (
                  <>
                    <PauseCircle className="h-3 w-3" />
                    Pause
                  </>
                )}
              </button>
              <button
                onClick={() => setCancelling(row)}
                className="flex items-center gap-1 text-xs text-destructive hover:underline"
              >
                <XCircle className="h-3 w-3" />
                Cancel
              </button>
            </span>
          </Can>
        ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">All statuses</option>
          {Object.entries(SUBSCRIPTION_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Can permission="product.subscription.create">
          <Button className="ml-auto" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            New subscription
          </Button>
        </Can>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load subscriptions.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        emptyTitle="No subscriptions yet"
        emptyDescription="Create a product and a plan first, then put a client on it."
        emptyAction={
          <Can permission="product.subscription.create">
            <Button onClick={() => setCreating(true)}>
              <Repeat className="h-4 w-4" />
              New subscription
            </Button>
          </Can>
        }
      />

      {creating && <NewSubscriptionDialog onClose={() => setCreating(false)} />}
      {cancelling && (
        <CancelDialog subscription={cancelling} onClose={() => setCancelling(null)} />
      )}
    </div>
  );
}

function NewSubscriptionDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateSubscription();
  const { data: companies = [] } = useCompanyOptions();
  const { data: products = [] } = useProducts();
  const [companyId, setCompanyId] = useState('');
  const [planId, setPlanId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [discount, setDiscount] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  // Only the current version of each plan can be sold.
  const plans = products.flatMap((product) =>
    product.plans
      .filter((plan) => plan.status === 'ACTIVE')
      .map((plan) => ({ ...plan, productName: product.name })),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New subscription</DialogTitle>
          <DialogDescription>
            The price is taken from the plan and frozen, so a later price change will not touch this
            client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Client</Label>
            <Select value={companyId} onChange={(event) => setCompanyId(event.target.value)}>
              <option value="">Choose a client</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Plan</Label>
            <Select value={planId} onChange={(event) => setPlanId(event.target.value)}>
              <option value="">Choose a plan</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.productName} — {plan.name} ({formatMoney(plan.price, plan.currency)}/
                  {BILLING_CYCLE_LABELS[plan.billingCycle].toLowerCase()})
                </option>
              ))}
            </Select>
            {plans.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No active plans yet — add one under Products and plans.
              </p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Quantity</Label>
              <Input
                type="number"
                min={1}
                value={quantity}
                onChange={(event) => setQuantity(Number(event.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Discount</Label>
              <Input
                inputMode="decimal"
                placeholder="0.00"
                value={discount}
                onChange={(event) => setDiscount(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Starts</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={create.isPending || !companyId || !planId}
            onClick={async () => {
              setError(null);
              try {
                await create.mutateAsync({
                  companyId,
                  planId,
                  quantity,
                  discount: discount || undefined,
                  startDate,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not create it.',
                );
              }
            }}
          >
            {create.isPending ? 'Creating…' : 'Create subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({
  subscription,
  onClose,
}: {
  subscription: Subscription;
  onClose: () => void;
}) {
  const cancel = useCancelSubscription();
  const [reason, setReason] = useState('');
  const [atPeriodEnd, setAtPeriodEnd] = useState(true);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel {subscription.number}?</DialogTitle>
          <DialogDescription>
            {subscription.company.name} — {subscription.product.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="flex cursor-pointer items-start gap-2.5 rounded-md border p-3">
            <Checkbox
              checked={atPeriodEnd}
              onCheckedChange={(checked) => setAtPeriodEnd(checked === true)}
            />
            <span className="text-sm">
              Let the current period run out
              <span className="block text-xs text-muted-foreground">
                Service continues until {formatDate(subscription.currentPeriodEnd)}. Unticking ends
                it today.
              </span>
            </span>
          </label>

          <div className="space-y-1.5">
            <Label>Why are they leaving?</Label>
            <Textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3 || cancel.isPending}
            onClick={async () => {
              await cancel.mutateAsync({ id: subscription.id, reason, atPeriodEnd });
              onClose();
            }}
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** "Oro Star CRM" -> "ORO-STAR-CRM", so nobody has to invent a code. */
function codeFrom(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 24);
}

const PRODUCT_TYPES = ['SAAS', 'SERVICE', 'AMC', 'ONE_TIME', 'ADDON', 'HARDWARE'] as const;
const PRODUCT_STATUSES = ['DRAFT', 'ACTIVE', 'SUNSET', 'RETIRED'] as const;

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  SAAS: 'SaaS subscription',
  SERVICE: 'Service',
  AMC: 'AMC / support contract',
  ONE_TIME: 'One-time sale',
  ADDON: 'Add-on',
  HARDWARE: 'Hardware',
};

const emptyProduct = {
  id: undefined as string | undefined,
  name: '',
  code: '',
  type: 'SAAS',
  category: '',
  status: 'ACTIVE',
  description: '',
};

const emptyPlan = {
  id: undefined as string | undefined,
  name: '',
  code: '',
  price: '',
  setupFee: '',
  billingCycle: 'MONTHLY' as BillingCycle,
  trialDays: 0,
  features: '',
};

/**
 * The catalogue: what we sell, and what each thing costs.
 *
 * A product is the thing itself — CRM, an AMC, a licence — and its plans are
 * the ways it is priced: once, monthly, yearly. Everything a client is buying
 * lives on the plan, including the list of what they actually get, because
 * that is the part somebody has to read out on a call.
 */
function CatalogueTab() {
  const { data: products = [], isLoading } = useProducts();
  const saveProduct = useSaveProduct();
  const savePlan = useSavePlan();

  const [productForm, setProductForm] = useState<typeof emptyProduct | null>(null);
  const [productError, setProductError] = useState<string | null>(null);
  const [planFor, setPlanFor] = useState<string | null>(null);
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [planError, setPlanError] = useState<string | null>(null);

  if (isLoading) return <TableSkeleton columns={3} />;

  const submitProduct = async () => {
    if (!productForm) return;
    setProductError(null);
    // A code nobody typed is a code made from the name, not a failed save.
    const code = productForm.code.trim() || codeFrom(productForm.name);
    try {
      await saveProduct.mutateAsync({ ...productForm, code });
      setProductForm(null);
    } catch (caught) {
      setProductError(
        caught instanceof ApiRequestError ? caught.message : 'Could not save the product.',
      );
    }
  };

  const submitPlan = async (productId: string) => {
    setPlanError(null);
    try {
      await savePlan.mutateAsync({
        ...planForm,
        productId,
        code: planForm.code.trim() || codeFrom(planForm.name),
        features: planForm.features
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      });
      setPlanFor(null);
      setPlanForm(emptyPlan);
    } catch (caught) {
      setPlanError(caught instanceof ApiRequestError ? caught.message : 'Could not save the plan.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission="product.product.create">
          <Button size="sm" variant="outline" onClick={() => setProductForm({ ...emptyProduct })}>
            <Plus className="h-4 w-4" />
            New product
          </Button>
        </Can>
      </div>

      {productForm && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {productForm.id ? 'Edit product' : 'New product'}
            </CardTitle>
            <CardDescription>
              What it is and how it is sold. Prices live on its plans, below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  autoFocus
                  value={productForm.name}
                  placeholder="Oro Star CRM"
                  onChange={(event) => setProductForm({ ...productForm, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Code</Label>
                <Input
                  value={productForm.code}
                  placeholder={codeFrom(productForm.name) || 'CRM'}
                  onChange={(event) => setProductForm({ ...productForm, code: event.target.value })}
                />
                <p className="text-[11px] text-muted-foreground">
                  Left blank, it is made from the name.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select
                  value={productForm.type}
                  onChange={(event) => setProductForm({ ...productForm, type: event.target.value })}
                >
                  {PRODUCT_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {PRODUCT_TYPE_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input
                  value={productForm.category}
                  placeholder="Software"
                  onChange={(event) =>
                    setProductForm({ ...productForm, category: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={productForm.status}
                  onChange={(event) =>
                    setProductForm({ ...productForm, status: event.target.value })
                  }
                >
                  {PRODUCT_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {value.charAt(0) + value.slice(1).toLowerCase()}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>What this is</Label>
              <Textarea
                rows={3}
                value={productForm.description}
                placeholder="One system for leads, deals, quotes, invoices and delivery. Hosted, updated and supported by us."
                onChange={(event) =>
                  setProductForm({ ...productForm, description: event.target.value })
                }
              />
            </div>

            <FormError message={productError} />

            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={saveProduct.isPending || productForm.name.trim().length < 2}
                onClick={submitProduct}
              >
                {saveProduct.isPending ? 'Saving…' : 'Save product'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setProductForm(null)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {products.length === 0 && !productForm ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Package className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No products yet. Add one, then give it a plan clients can subscribe to.
            </p>
          </CardContent>
        </Card>
      ) : (
        products.map((product) => (
          <Card key={product.id}>
            <CardHeader className="flex-row items-start justify-between pb-3">
              <div className="min-w-0">
                <CardTitle className="text-base">
                  {product.name}
                  <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
                    {product.code}
                  </span>
                  {product.status !== 'ACTIVE' && (
                    <Badge variant="secondary" className="ml-2 text-[10px]">
                      {product.status}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {PRODUCT_TYPE_LABELS[product.type] ?? product.type.replace(/_/g, ' ')}
                  {product.category ? ` · ${product.category}` : ''} · {product.subscriptionCount}{' '}
                  {product.subscriptionCount === 1 ? 'subscriber' : 'subscribers'}
                </CardDescription>
                {product.description && (
                  <p className="mt-2 max-w-2xl whitespace-pre-wrap text-sm text-muted-foreground">
                    {product.description}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Can permission="product.product.update">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setProductError(null);
                      setProductForm({
                        id: product.id,
                        name: product.name,
                        code: product.code,
                        type: product.type,
                        category: product.category ?? '',
                        status: product.status,
                        description: product.description ?? '',
                      });
                    }}
                  >
                    Edit
                  </Button>
                </Can>
                <Can permission="product.plan.create">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setPlanError(null);
                      setPlanForm(emptyPlan);
                      setPlanFor(product.id);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Plan
                  </Button>
                </Can>
              </div>
            </CardHeader>
            <CardContent>
              {product.plans.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No plans yet — add one to say what it costs.
                </p>
              ) : (
                <ul className="space-y-2">
                  {product.plans.map((plan) => (
                    <li key={plan.id} className="rounded-md border px-3 py-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-sm font-medium">
                          {plan.name}
                          {plan.version > 1 && (
                            <Badge variant="secondary" className="ml-2 text-[10px]">
                              v{plan.version}
                            </Badge>
                          )}
                          <span className="ml-2 font-mono text-[11px] font-normal text-muted-foreground">
                            {plan.code}
                          </span>
                        </span>
                        <span className="tabular text-sm font-medium">
                          {formatMoney(plan.price, plan.currency)}
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            {plan.billingCycle === 'ONE_TIME'
                              ? 'one-time'
                              : `/ ${BILLING_CYCLE_LABELS[plan.billingCycle].toLowerCase()}`}
                          </span>
                        </span>
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {plan.setupFee && plan.setupFee !== '0.00' && (
                          <span>Setup {formatMoney(plan.setupFee, plan.currency)}</span>
                        )}
                        {plan.trialDays > 0 && <span>{plan.trialDays}-day trial</span>}
                        <span>
                          {plan.subscriptionCount}{' '}
                          {plan.subscriptionCount === 1 ? 'client' : 'clients'} on this version
                        </span>
                      </div>

                      {plan.features.length > 0 && (
                        <ul className="mt-2 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex gap-1.5 text-xs text-muted-foreground">
                              <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {planFor === product.id && (
                <div className="mt-3 space-y-3 rounded-md border p-3">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Plan name</Label>
                      <Input
                        className="h-8"
                        autoFocus
                        placeholder="Standard"
                        value={planForm.name}
                        onChange={(event) => setPlanForm({ ...planForm, name: event.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Code</Label>
                      <Input
                        className="h-8"
                        placeholder={codeFrom(planForm.name) || 'STANDARD'}
                        value={planForm.code}
                        onChange={(event) => setPlanForm({ ...planForm, code: event.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">How it is charged</Label>
                      <Select
                        className="h-8 text-xs"
                        value={planForm.billingCycle}
                        onChange={(event) =>
                          setPlanForm({
                            ...planForm,
                            billingCycle: event.target.value as BillingCycle,
                          })
                        }
                      >
                        {Object.entries(BILLING_CYCLE_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {value === 'ONE_TIME'
                              ? 'One-time charge'
                              : `Every ${label.toLowerCase()}`}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Price</Label>
                      <Input
                        className="h-8"
                        inputMode="decimal"
                        placeholder="1500.00"
                        value={planForm.price}
                        onChange={(event) => setPlanForm({ ...planForm, price: event.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">One-off setup fee</Label>
                      <Input
                        className="h-8"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={planForm.setupFee}
                        onChange={(event) =>
                          setPlanForm({ ...planForm, setupFee: event.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Free trial (days)</Label>
                      <Input
                        className="h-8"
                        type="number"
                        min={0}
                        value={planForm.trialDays}
                        onChange={(event) =>
                          setPlanForm({ ...planForm, trialDays: Number(event.target.value) || 0 })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">What the client gets — one per line</Label>
                    <Textarea
                      rows={4}
                      placeholder={'Unlimited users\nWhatsApp and email\n12 support visits a year\nDaily backups'}
                      value={planForm.features}
                      onChange={(event) =>
                        setPlanForm({ ...planForm, features: event.target.value })
                      }
                    />
                  </div>

                  <FormError message={planError} />

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={
                        savePlan.isPending || !planForm.name.trim() || !planForm.price.trim()
                      }
                      onClick={() => void submitPlan(product.id)}
                    >
                      {savePlan.isPending ? 'Saving…' : 'Save plan'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPlanFor(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
